import { useState } from 'react';
import type { ReactNode } from 'react';
import { LogoutOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { Avatar, Button, Flex, Grid, Popover, theme, Typography } from 'antd';
import { useColorScheme } from './color-scheme';
import { brandColor, coresDaOferta } from './theme';
import { iniciaisDoNome } from './perfil';

/**
 * Perfil no rodapé da barra lateral.
 *
 * É onde o SaaS costuma guardar o que é do usuário e não do produto: quem está
 * logado, tema e sair. Tirar isso do topo devolve a faixa superior inteira ao
 * conteúdo - no celular, são 64px a mais de leitura.
 *
 * Vive no pacote compartilhado porque app e admin usam o mesmo rodapé: duplicar
 * significaria manter dois menus de conta em sincronia.
 *
 * O cartão abre no hover e no clique: no desktop o hover é o gesto esperado; no
 * toque, hover não existe.
 *
 * **Cartão, e não menu.** Era um `Dropdown` que subia por cima da barra
 * (`placement="topRight"`), estreito e com o nome espremido numa linha
 * desativada do menu. O desenho agora é o da Voa, medido nela em 06/09/2026:
 * um `Popover` em `rightBottom` que sai **ao lado** da barra, 240px de largura,
 * com um cabeçalho próprio - avatar grande, nome e e-mail centrados sobre um
 * fundo levemente tingido - e as ações embaixo, uma por linha. O ganho não é
 * enfeite: quem abre o menu da conta quer primeiro **conferir em qual conta
 * está**, e no menu antigo esse era o item que não dava para ler inteiro.
 */

/** Uma ação do cartão: o que o produto oferece ali dentro. */
interface AcaoDoPerfil {
  key: string;
  icon?: ReactNode;
  label: string;
  onClick: () => void;
  /** Vermelho, para o que encerra a sessão. */
  perigo?: boolean;
}

export function PerfilDaBarra({
  nome,
  email,
  foto,
  etiqueta,
  recolhida,
  onSair,
  itensExtras = [],
}: {
  nome?: string;
  email?: string;
  /** A foto do perfil, quando há - hoje só vem do Google. */
  foto?: string;
  /**
   * Uma palavra sobre a conta: o plano do aluno.
   *
   * O componente a desenha de dois jeitos - selo sobre a foto na barra,
   * etiqueta no cabeçalho do cartão -, e é por isso que ele recebe o texto e
   * não um `ReactNode` pronto: quem chama não sabe o espaço de cada lugar.
   *
   * `premium` troca o verde chapado pelo degradê das três cores dos produtos,
   * o mesmo da chamada da oferta. É o que separa "Pro" de "Grátis" sem precisar
   * de mais nada na tela.
   */
  etiqueta?: { rotulo: string; premium?: boolean };
  recolhida: boolean;
  onSair: () => void;
  /**
   * Itens do produto, entre o cabeçalho e o tema.
   *
   * Opcional porque este componente é dos dois apps, e o que o aluno tem
   * ali - a assinatura dele - não faz sentido no backoffice. Passar por
   * fora é mais honesto que um `if (app)` dentro de um componente comum.
   */
  itensExtras?: { key: string; icon?: ReactNode; label: string; onClick: () => void }[];
}) {
  const { token } = theme.useToken();
  const { scheme, toggle } = useColorScheme();
  const escuro = scheme === 'dark';

  /*
   * No celular a barra é uma gaveta de 260px sobre uma tela de 390, e o cartão
   * tem 240: ao lado, ele sairia 114px para fora da janela - medido em
   * 06/09/2026, e o `autoAdjustOverflow` do Antd não o traz de volta. Lá ele
   * sobe, que é o que a gaveta tem de espaço.
   */
  const telas = Grid.useBreakpoint();
  const estreita = !telas.md;

  /*
   * O cartão é controlado porque o `Popover` não fecha sozinho quando se
   * clica dentro dele - o `Dropdown` fechava, e sem isto "Configurações"
   * navegaria com o cartão aberto por cima da tela nova.
   */
  const [aberto, setAberto] = useState(false);

  const iniciais = iniciaisDoNome(nome, email);

  const acoes: AcaoDoPerfil[] = [
    ...itensExtras,
    {
      key: 'tema',
      icon: escuro ? <SunOutlined /> : <MoonOutlined />,
      label: escuro ? 'Modo claro' : 'Modo escuro',
      onClick: toggle,
    },
    { key: 'sair', icon: <LogoutOutlined />, label: 'Sair', onClick: onSair, perigo: true },
  ];

  const cartao = (
    <Flex vertical style={{ width: 240 }}>
      {/*
        O cabeçalho encosta nos cantos de cima do cartão - daí o `padding: 0`
        no corpo do Popover e o raio repetido aqui. Sem os dois, o fundo tingido
        vira um retângulo flutuando dentro de outro retângulo.
      */}
      <Flex
        vertical
        align="center"
        gap={8}
        style={{
          padding: '20px 16px 16px',
          background: token.colorFillQuaternary,
          borderRadius: `${token.borderRadiusLG}px ${token.borderRadiusLG}px 0 0`,
        }}
      >
        {/*
          O selo fica **sobre a foto**, e não numa etiqueta solta embaixo do
          e-mail: é onde ele está na barra, dois centímetros abaixo, e é onde a
          Voa põe o dela. Solto, ele lia como mais uma linha do cartão - e o
          cartão já tem nome, e-mail e três ações para ler.
        */}
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <ComAnelDoPro premium={etiqueta?.premium}>
            <Avatar
              size={etiqueta?.premium ? 48 : 52}
              src={foto}
              alt={nome ?? email}
              style={{ fontSize: token.fontSizeLG, ...fundoDentroDoAnel(etiqueta?.premium, token) }}
            >
              {iniciais}
            </Avatar>
          </ComAnelDoPro>
          {etiqueta ? <SeloDoPlano {...etiqueta} grande /> : null}
        </div>

        {/*
          `maxWidth: '100%'` nos dois textos: sem isso o `ellipsis` do Antd não
          tem contra o que cortar dentro de uma coluna centrada, e um e-mail
          longo estica o cartão inteiro.
        */}
        <Flex vertical align="center" style={{ maxWidth: '100%' }}>
          <Typography.Text strong ellipsis style={{ maxWidth: '100%' }}>
            {nome ?? 'Minha conta'}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            ellipsis
            style={{ maxWidth: '100%', fontSize: token.fontSizeSM }}
          >
            {email}
          </Typography.Text>
        </Flex>

      </Flex>

      <Flex vertical style={{ padding: 4 }}>
        {acoes.map((acao) => (
          <Button
            key={acao.key}
            type="text"
            block
            danger={acao.perigo}
            icon={acao.icon}
            style={{ justifyContent: 'flex-start' }}
            onClick={() => {
              setAberto(false);
              acao.onClick();
            }}
          >
            {acao.label}
          </Button>
        ))}
      </Flex>
    </Flex>
  );

  return (
    <Popover
      open={aberto}
      onOpenChange={setAberto}
      trigger={['hover', 'click']}
      placement={estreita ? 'topLeft' : 'rightBottom'}
      arrow={false}
      /*
        O cartão nunca encosta na borda da janela. Ao lado da barra, o
        `rightBottom` do Antd casa o pé dele com o pé do gatilho - que é a
        última linha da barra -, então ele sobe oito; na gaveta, o `topLeft`
        casa a borda esquerda com a da gaveta, que é a da tela, então ele anda
        oito para dentro. A Voa afasta os mesmos oito.
      */
      align={{ offset: estreita ? [8, -4] : [4, -8] }}
      content={cartao}
      styles={{ container: { padding: 0 } }}
    >
      <Flex
        align="center"
        gap={10}
        style={{
          padding: recolhida ? '12px 0' : '12px 16px',
          justifyContent: recolhida ? 'center' : 'flex-start',
          cursor: 'pointer',
        }}
        role="button"
        tabIndex={0}
        aria-label="Minha conta"
      >
        {/*
          Foto quando há, iniciais quando não há - e **nunca o boneco genérico**,
          que era o que aparecia para todo mundo. O `icon` estava junto das
          iniciais, e no Antd o ícone vence: a variável das iniciais existia e
          não chegava à tela.
        */}
        {/*
          **O selo fica sobre a foto**, como na Voa - e o avatar subiu de 24
          para 32 por causa dele: "Grátis" tem seis letras, e sobre um disco de
          24 a pastilha avançava nove pixels de cada lado e encostava no nome.
          O anel na cor do fundo é o que separa o selo da foto por baixo; sem
          ele, os dois viram uma mancha só quando a foto é escura.
        */}
        <div style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
          <ComAnelDoPro premium={etiqueta?.premium}>
            <Avatar
              size={etiqueta?.premium ? 28 : 32}
              src={foto}
              alt={nome ?? email}
              style={fundoDentroDoAnel(etiqueta?.premium, token)}
            >
              {iniciais}
            </Avatar>
          </ComAnelDoPro>
          {etiqueta ? <SeloDoPlano {...etiqueta} /> : null}
        </div>

        {recolhida ? null : (
          <div style={{ minWidth: 0 }}>
            <Typography.Text ellipsis style={{ display: 'block', lineHeight: 1.2 }}>
              {nome ?? 'Minha conta'}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              ellipsis
              style={{ display: 'block', fontSize: 12, lineHeight: 1.2 }}
            >
              {email}
            </Typography.Text>
          </div>
        )}
      </Flex>
    </Popover>
  );
}

/**
 * O pé da barra lateral: o fio fino, e tudo o que vem depois dele.
 *
 * **O fio era do perfil**, e por isso ele nascia colado na última linha da
 * barra, com o atalho e a chamada da oferta soltos acima dele - do lado de
 * fora do bloco que eles compõem. Na Voa a linha vem antes do primeiro botão
 * do pé, e é ela que separa a navegação do que é do usuário.
 *
 * Vive aqui, e não em cada layout, porque os dois apps têm o mesmo pé - o do
 * backoffice tem só o perfil dentro.
 */
export function RodapeDaBarra({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();

  return (
    <Flex vertical style={{ borderTop: `1px solid ${token.colorSplit}`, paddingTop: 8 }}>
      {children}
    </Flex>
  );
}

/**
 * O fundo do avatar quando ele mora dentro do anel.
 *
 * **Sem isto o anel não é anel.** O fundo padrão do `Avatar` do Antd é
 * translúcido (`rgba(20, 28, 27, 0.25)`), então o degradê aparecia *através*
 * das iniciais e o conjunto virava um disco colorido em vez de uma foto com
 * borda. Opaco, o degradê fica onde devia: só na borda.
 */
function fundoDentroDoAnel(premium: boolean | undefined, token: { colorBgContainer: string; colorText: string }) {
  return premium ? { background: token.colorBgContainer, color: token.colorText } : {};
}

/**
 * O anel de quem paga, em volta da foto.
 *
 * Dois pixels do degradê dos produtos, e o avatar encolhe na mesma medida para
 * o conjunto continuar do tamanho de antes - a linha do rodapé não muda de
 * altura por causa dele.
 *
 * É o segundo sinal do plano pago, e existe porque o primeiro (o selo) some no
 * cartão da conta, onde não há selo. Quem paga vê a assinatura da marca em
 * volta da própria foto nos dois lugares; quem não paga vê a foto limpa.
 */
function ComAnelDoPro({ premium, children }: { premium?: boolean; children: ReactNode }) {
  if (!premium) return <>{children}</>;

  return (
    <span
      style={{
        display: 'inline-flex',
        padding: 2,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${coresDaOferta.join(', ')})`,
        lineHeight: 0,
      }}
    >
      {children}
    </span>
  );
}

/**
 * O selo sobre a foto, como o "FREE" da Voa.
 *
 * **Cheio no verde da marca, e não a etiqueta clara do Antd.** Sobre uma foto
 * qualquer, uma pastilha translúcida some; a cor sólida com texto branco vale
 * em cima de foto clara, de foto escura e das iniciais. O verde é o
 * `brandColor` nos dois temas, e não o `colorPrimary`: no escuro ele é clareado
 * para `#33b7b1`, onde o branco cai para 2,1:1 - num texto de nove pixels isso
 * é ilegível. No tom original o contraste é 4,9:1.
 *
 * O anel é da cor do fundo da barra, o mesmo truque do `Badge` do Antd: é ele
 * que descola o selo da foto por baixo.
 */
function SeloDoPlano({
  rotulo,
  premium,
  grande,
}: {
  rotulo: string;
  premium?: boolean;
  /** A medida do cartão, onde a foto tem 52px em vez de 32. */
  grande?: boolean;
}) {
  const { token } = theme.useToken();

  return (
    <span
      style={{
        position: 'absolute',
        insetInlineStart: '50%',
        bottom: grande ? -8 : -6,
        transform: 'translateX(-50%)',
        paddingInline: grande ? 7 : 5,
        borderRadius: 999,
        border: `2px solid ${token.colorBgContainer}`,
        /*
         * Quem paga leva o degradê das três cores dos produtos - o mesmo da
         * chamada que ele não vê mais. É a única coisa colorida do rodapé de
         * uma conta paga, e é de propósito: o prêmio de ter comprado não pode
         * ser só a ausência dos avisos de quem não comprou.
         */
        background: premium
          ? `linear-gradient(135deg, ${coresDaOferta.join(', ')})`
          : brandColor,
        color: '#fff',
        fontSize: grande ? 11 : 9,
        lineHeight: grande ? '18px' : '14px',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {rotulo}
    </span>
  );
}
